import {
  ModelScreenerBasicFilters,
  ModelScreenerFilterDropdownLov,
  ModelScreenerFilterDTO,
  ModelScreenerFilterItemDataDTO,
  ModelScreenerFilterItemDTO,
  ModelScreenerFilterItemOptionDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { FieldPath } from "react-hook-form";
import { cloneDeep, defaultTo, first, isEmpty, isEqual, isNil } from "lodash";
import { ModelScreenerFilterItemType } from "@models/model-screener/enums/filterType";
import React from "react";
import { MSFilterSwitchYesNo } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/switch-yes-no";
import { MSFilterSwitchDropdown } from "src/pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/switch-dropdown";
import { MSFilterSlider } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/slider";
import { MSFilterTrackRecord } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/track-record";
import { MSFilterSwitchSelect } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/switch-select";
import {
  MSCellDataConstants,
  MSDefaultDateFormat,
  MSSubHeaderIdConstants,
} from "@pages/managed-accounts/model-screener/constant";
import {
  ModelScreenerData,
  ModelScreenerRow,
} from "@models/model-screener/entities/modelScreenerData";
import {
  MSFilterFavouriteSwitchValues,
  MSFilterSwitchValues,
  MSSelectedFilterValues,
} from "@models/model-screener/enums/filterValues";
import { MSFilterCombinedSlider } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/combined-slider";
import { MSFilterDefaultValueConstants } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { differenceInYears, parse } from "date-fns";
import { generateUUID } from "@utils";
import { compareSearchValues } from "@pages/managed-accounts/util";
import { MSFilterFavourite } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/favourite";

export interface MSTableFilterItemRef {
  reset?: () => void;
  validate?: () => void;
}

export type MSFilterItemProps = {
  item: ModelScreenerFilterItemDTO;
  itemPaths?: any;
  itemSize?: "small" | "medium";
  disabled?: boolean;
};

export type MSFilterRenderedItemProps = MSFilterItemProps & {
  ref?: React.ForwardedRef<MSTableFilterItemRef>;
  disabled?: boolean;
  allowOverflow?: boolean;
};

// General field paths
export type EditMSFilterPath = FieldPath<ModelScreenerFilterDTO>;
export type EditMSFilterColumnPath = {
  sections: EditMSFilterPath;
};
export type EditMSFilterSectionPath = {
  items: EditMSFilterPath;
};
export type EditMSFilterItemPath = {
  data: EditMSFilterPath;
};
export type EditMSFilterItemDataPath = {
  currentMinValue: EditMSFilterPath;
  currentMaxValue: EditMSFilterPath;
  switchValue: EditMSFilterPath;
  options: EditMSFilterPath;
  lov: EditMSFilterPath;
  nestedData: EditMSFilterPath;
};
export type EditMSFilterItemDataOptionsPath = {
  targetSubHeaderId: EditMSFilterPath;
  label: EditMSFilterPath;
  lov: EditMSFilterPath;
};

// Rendered field path
export type EditMSFilterSwitchYesNoFieldPath = {
  switchValue: EditMSFilterPath;
  lov: EditMSFilterPath;
};
export type EditMSFilterSwitchDropdownFieldPath = {
  switchValue: EditMSFilterPath;
  options: EditMSFilterPath[];
};
export type EditMSFilterSwitchSelectFieldPath = {
  switchValue: EditMSFilterPath;
  lov: EditMSFilterPath;
};
export type EditMSFilterSliderFieldPath = {
  currentMinValue: EditMSFilterPath;
  currentMaxValue: EditMSFilterPath;
};
export type EditMSFilterTrackRecordFieldPath = {
  lov: EditMSFilterPath;
};
export type EditMSFilterCombinedSliderFieldPath = {
  lov: EditMSFilterPath;
  sliderPaths: Record<string, EditMSFilterSliderFieldPath[]>;
};
export type EditMSFilterFavouriteFieldPath = {
  switchValue: EditMSFilterPath;
};

export type EditMSFilterCheckBoxButtonFieldPath = {
  shouldIncludeNA: EditMSFilterPath;
  shouldIncludeNoData: EditMSFilterPath;
};

// Item initial props
export type MSFilterSwitchYesNoInitialProps = {
  switchLabel?: string | null;
  option?: ModelScreenerFilterItemOptionDTO | null;
  defaultDropdownOption?: ModelScreenerFilterDropdownLov[] | null;
};
export type MSFilterSwitchDropdownInitialProps = {
  switchLabel?: string | null;
  options?: ModelScreenerFilterItemOptionDTO[] | null;
  defaultDropdownOptions?: ModelScreenerFilterDropdownLov[][] | null;
};
export type MSFilterSliderInitialProps = {
  label?: string | null;
  fieldType?: string | null;
  minValue: number | null;
  maxValue: number | null;
  stepValue: number | null;
};
export type MSFilterTrackRecordInitialProps = {
  label?: string | null;
  defaultDropdownOptions?: ModelScreenerFilterDropdownLov[] | null;
};
export type MSFilterSwitchSelectInitialProps = {
  switchLabel?: string | null;
  optionLabel?: string | null;
  defaultSelectOptions?: ModelScreenerFilterDropdownLov[] | null;
};
export type MSFilterCombinedSliderInitialProps = {
  selectDropdownLabel?: string | null;
  defaultSelectOptions?: ModelScreenerFilterDropdownLov[] | null;
  sliderItemProps?: Record<string, ModelScreenerFilterItemDataDTO[]> | null;
};

// Functions
export const getMSFilterColumnPath = ({
  columnIndex,
}: {
  columnIndex: number;
}): EditMSFilterColumnPath => ({
  sections: `columns.${columnIndex}.sections` as EditMSFilterPath,
});

export const getMSFilterSectionPath = ({
  columnIndex,
  sectionIndex,
}: {
  columnIndex: number;
  sectionIndex: number;
}): EditMSFilterSectionPath => {
  const columnSectionsPath = getMSFilterColumnPath({ columnIndex }).sections;
  return {
    items: `${columnSectionsPath}.${sectionIndex}.items` as EditMSFilterPath,
  };
};

export const getMSFilterCheckboxButtonPaths =
  (): EditMSFilterCheckBoxButtonFieldPath => {
    return {
      shouldIncludeNA: "shouldIncludeNa" as EditMSFilterPath,
      shouldIncludeNoData: "shouldIncludeNoData" as EditMSFilterPath,
    };
  };

export const getMSFilterItemPath = ({
  columnIndex,
  sectionIndex,
  itemIndex,
}: {
  columnIndex: number;
  sectionIndex: number;
  itemIndex: number;
}): EditMSFilterItemPath => {
  const sectionItemsPath = getMSFilterSectionPath({
    columnIndex,
    sectionIndex,
  }).items;
  return {
    data: `${sectionItemsPath}.${itemIndex}.data` as EditMSFilterPath,
  };
};

export const getMSFilterItemDataFieldPath = ({
  columnIndex,
  sectionIndex,
  itemIndex,
}: GetFieldPathsParams): EditMSFilterItemDataPath => {
  const itemPath = getMSFilterItemPath({
    columnIndex,
    sectionIndex,
    itemIndex,
  });

  return {
    currentMinValue: `${itemPath.data}.currentMinValue` as EditMSFilterPath,
    currentMaxValue: `${itemPath.data}.currentMaxValue` as EditMSFilterPath,
    switchValue: `${itemPath.data}.switchValue` as EditMSFilterPath,
    options: `${itemPath.data}.options` as EditMSFilterPath,
    lov: `${itemPath.data}.lov` as EditMSFilterPath,
    nestedData: `${itemPath.data}.nestedData` as EditMSFilterPath,
  };
};

export const getMSFilterItemDataOptionsPaths = ({
  indexes,
  numOfOptions,
  itemDataPath,
}: {
  indexes: GetFieldPathsParams;
  numOfOptions: number;
  itemDataPath?: EditMSFilterItemDataPath;
}): EditMSFilterItemDataOptionsPath[] => {
  const { columnIndex, sectionIndex, itemIndex } = indexes;
  const targetItemDataPath =
    itemDataPath ??
    getMSFilterItemDataFieldPath({
      columnIndex,
      sectionIndex,
      itemIndex,
    });

  const result: EditMSFilterItemDataOptionsPath[] = [];
  for (let i = 0; i < numOfOptions; i++) {
    const optionPath = `${targetItemDataPath.options}.${i}` as EditMSFilterPath;

    result.push({
      targetSubHeaderId: `${optionPath}.targetSubHeaderId` as EditMSFilterPath,
      label: `${optionPath}.label` as EditMSFilterPath,
      lov: `${optionPath}.lov` as EditMSFilterPath,
    });
  }

  return result;
};

type GetFieldPathsParams = {
  columnIndex: number;
  sectionIndex: number;
  itemIndex: number;
};

type GetFieldPathsParamsWithOptions = {
  indexes: GetFieldPathsParams;
  numOfOptions: number;
};

export const getMSFilterSwitchYesNoFieldPath = (
  indexes: GetFieldPathsParams,
): EditMSFilterSwitchYesNoFieldPath => {
  const itemDataPath = getMSFilterItemDataFieldPath(indexes);

  const itemDataOptionsPath = getMSFilterItemDataOptionsPaths({
    indexes,
    numOfOptions: 1,
    itemDataPath,
  });

  return {
    switchValue: itemDataPath.switchValue,
    lov: itemDataOptionsPath[0].lov,
  };
};

export const getMSFilterSwitchDropdownFieldPath = ({
  indexes,
  numOfOptions,
}: GetFieldPathsParamsWithOptions): EditMSFilterSwitchDropdownFieldPath => {
  const itemDataPath = getMSFilterItemDataFieldPath(indexes);
  const itemDataOptionsPaths = getMSFilterItemDataOptionsPaths({
    indexes,
    numOfOptions,
  });

  return {
    switchValue: itemDataPath.switchValue,
    options: itemDataOptionsPaths?.map((path) => path.lov),
  };
};

export const getMSFilterSliderFieldPath = (
  indexes: GetFieldPathsParams,
): EditMSFilterSliderFieldPath => {
  const itemDataPath = getMSFilterItemDataFieldPath(indexes);

  return {
    currentMinValue: itemDataPath.currentMinValue,
    currentMaxValue: itemDataPath.currentMaxValue,
  };
};

export const getMSFilterTrackRecordFieldPath = (
  indexes: GetFieldPathsParams,
): EditMSFilterTrackRecordFieldPath => {
  const itemDataPath = getMSFilterItemDataFieldPath(indexes);

  return {
    lov: itemDataPath.lov,
  };
};

export const getMSFilterSwitchSelectFieldPath = (
  indexes: GetFieldPathsParams,
): EditMSFilterSwitchSelectFieldPath => {
  const itemDataPath = getMSFilterItemDataFieldPath(indexes);
  const optionsPaths = getMSFilterItemDataOptionsPaths({
    indexes,
    numOfOptions: 1,
    itemDataPath,
  });

  return {
    switchValue: itemDataPath.switchValue,
    lov: optionsPaths[0].lov,
  };
};

const getNestedDataFieldPath = ({
  nestedDataFieldPath,
  nestedData,
}: {
  nestedDataFieldPath: EditMSFilterPath;
  nestedData?: Record<string, ModelScreenerFilterItemDataDTO[]> | null;
}): Record<string, EditMSFilterSliderFieldPath[]> => {
  const result: Record<string, EditMSFilterSliderFieldPath[]> = {};

  if (!nestedData || isEmpty(nestedData)) {
    return result;
  }

  Object.keys(nestedData).forEach((nestedDataKey) => {
    const data = nestedData[nestedDataKey];

    result[nestedDataKey] = data.map((_, itemIndex) => {
      const itemIndexPath =
        `${nestedDataFieldPath}.${nestedDataKey}.${itemIndex}` as EditMSFilterPath;
      return {
        currentMinValue: `${itemIndexPath}.currentMinValue` as EditMSFilterPath,
        currentMaxValue: `${itemIndexPath}.currentMaxValue` as EditMSFilterPath,
      };
    });
  });

  return result;
};

export const getMSFilterCombinedSliderFieldPath = ({
  indexes,
  nestedData,
}: {
  indexes: GetFieldPathsParams;
  nestedData?: Record<string, ModelScreenerFilterItemDataDTO[]> | null;
}): EditMSFilterCombinedSliderFieldPath => {
  const itemDataPath = getMSFilterItemDataFieldPath(indexes);
  const nestedDataPaths = getNestedDataFieldPath({
    nestedDataFieldPath: itemDataPath.nestedData,
    nestedData,
  });

  return {
    lov: itemDataPath.lov,
    sliderPaths: nestedDataPaths,
  };
};

export const getMSFilterFavouriteFieldPath = (
  indexes: GetFieldPathsParams,
): EditMSFilterFavouriteFieldPath => ({
  switchValue: getMSFilterItemDataFieldPath(indexes).switchValue,
});

export const getPreComputeModelScreenerFilterData = ({
  data,
}: {
  data: ModelScreenerFilterDTO;
}): {
  compiledFieldPathRecord: Record<string, any>;
} => {
  const compiledFieldPathRecord: Record<string, any> = {};

  if (isEmpty(data)) {
    return {
      compiledFieldPathRecord,
    };
  }

  const columns = data.columns || [];
  columns.forEach((col, colIdx) => {
    const sections = col.sections || [];
    sections.forEach((sec, secIdx) => {
      const items = sec.items || [];
      items.forEach((item, itemIdx) => {
        const { id, filterType } = item;
        if (!id || !filterType) return;

        const indexes: GetFieldPathsParams = {
          columnIndex: colIdx,
          sectionIndex: secIdx,
          itemIndex: itemIdx,
        };

        switch (filterType) {
          case ModelScreenerFilterItemType.SwitchYesNo: {
            compiledFieldPathRecord[id] =
              getMSFilterSwitchYesNoFieldPath(indexes);
            break;
          }
          case ModelScreenerFilterItemType.SwitchDropdown: {
            compiledFieldPathRecord[id] = getMSFilterSwitchDropdownFieldPath({
              indexes,
              numOfOptions: item.data.options?.length || 0,
            });
            break;
          }
          case ModelScreenerFilterItemType.SwitchSelect: {
            compiledFieldPathRecord[id] =
              getMSFilterSwitchSelectFieldPath(indexes);
            break;
          }
          case ModelScreenerFilterItemType.Range: {
            compiledFieldPathRecord[id] = getMSFilterSliderFieldPath(indexes);
            break;
          }
          case ModelScreenerFilterItemType.TrackRecord: {
            compiledFieldPathRecord[id] =
              getMSFilterTrackRecordFieldPath(indexes);
            break;
          }
          case ModelScreenerFilterItemType.PerformancePeriod: {
            const nestedData = item.data?.nestedData;
            compiledFieldPathRecord[id] = getMSFilterCombinedSliderFieldPath({
              indexes,
              nestedData,
            });
            break;
          }
          case ModelScreenerFilterItemType.SwitchFavourite: {
            compiledFieldPathRecord[id] =
              getMSFilterFavouriteFieldPath(indexes);
            break;
          }
          default:
            break;
        }
      });
    });
  });

  return {
    compiledFieldPathRecord,
  };
};

const getMSFilterSwitchYesNoItemInitialProps = (
  item: ModelScreenerFilterItemDTO,
): MSFilterSwitchYesNoInitialProps => {
  const switchLabel = item?.data?.label;
  const option = first(item?.data?.options);
  const defaultDropdownOption = getSingleSelectDropdownDefaultOption({
    lov: option?.lov,
    allowNull: true,
  });

  return {
    switchLabel: switchLabel,
    option: option,
    defaultDropdownOption,
  };
};

const getMSFilterSwitchDropdownItemInitialProps = (
  item: ModelScreenerFilterItemDTO,
): MSFilterSwitchDropdownInitialProps => {
  const switchLabel = item?.data?.label;
  const options = item?.data?.options;
  const dropdownOptions: ModelScreenerFilterDropdownLov[][] =
    options?.map((item) => {
      return item.lov || [];
    }) || [];
  const defaultDropdownOptions: ModelScreenerFilterDropdownLov[][] =
    dropdownOptions.map(
      (lov) => getSingleSelectDropdownDefaultOption({ lov }) ?? lov,
    );

  return {
    switchLabel: switchLabel,
    options: options,
    defaultDropdownOptions: defaultDropdownOptions,
  };
};

const getMSFilterSliderItemInitialProps = (
  item: ModelScreenerFilterItemDTO,
): MSFilterSliderInitialProps => {
  const itemData = item?.data || {};

  const minValue = itemData?.minValue;
  const formattedMinValue: number | null = minValue ? Number(minValue) : null;
  const maxValue = itemData?.maxValue;
  const formattedMaxValue: number | null = maxValue ? Number(maxValue) : null;
  const stepValue = itemData?.stepValue;
  const formattedStepValue: number | null = stepValue
    ? parseFloat(String(stepValue))
    : null;

  return {
    label: itemData?.label,
    fieldType: itemData?.fieldType,
    minValue: formattedMinValue,
    maxValue: formattedMaxValue,
    stepValue: formattedStepValue,
  };
};

const getMSFilterTrackRecordItemInitialProps = (
  item: ModelScreenerFilterItemDTO,
): MSFilterTrackRecordInitialProps => {
  const itemData = item?.data || {};
  const defaultDropdownOptions =
    getSingleSelectDropdownDefaultOption({
      lov: itemData?.lov,
    }) || [];

  return {
    label: itemData?.label,
    defaultDropdownOptions: defaultDropdownOptions,
  };
};

const getMSFilterSwitchSelectItemInitialProps = (
  item: ModelScreenerFilterItemDTO,
): MSFilterSwitchSelectInitialProps => {
  const switchLabel = item?.data?.label;
  const option = first(item?.data?.options);
  const defaultSelectOptions = getSingleSelectDropdownDefaultOption({
    lov: option?.lov,
    allowNull: true,
  });

  return {
    switchLabel: switchLabel,
    optionLabel: option?.label,
    defaultSelectOptions: defaultSelectOptions,
  };
};

const getCombinedSliderItems = (
  nestedData?: Record<string, ModelScreenerFilterItemDataDTO[]> | null,
): Record<string, ModelScreenerFilterItemDataDTO[]> => {
  const result: Record<string, ModelScreenerFilterItemDataDTO[]> = {};
  if (!nestedData || isEmpty(nestedData)) return result;

  Object.keys(nestedData)?.forEach((nestedDataKey) => {
    result[nestedDataKey] = nestedData[nestedDataKey];
  });

  return result;
};

const getMSFilterCombinedSliderInitialProps = (
  item: ModelScreenerFilterItemDTO,
): MSFilterCombinedSliderInitialProps => {
  const itemData = item?.data || {};
  const defaultSelectOptions = getSingleSelectDropdownDefaultOption({
    lov: itemData?.lov,
    allowNull: true,
  });

  return {
    selectDropdownLabel: itemData?.label,
    defaultSelectOptions,
    sliderItemProps: getCombinedSliderItems(itemData?.nestedData),
  };
};

const RenderItemComponentMap: Record<
  ModelScreenerFilterItemType | string,
  React.ComponentType<MSFilterRenderedItemProps> | null
> = {
  [ModelScreenerFilterItemType.SwitchYesNo]: MSFilterSwitchYesNo,
  [ModelScreenerFilterItemType.SwitchDropdown]: MSFilterSwitchDropdown,
  [ModelScreenerFilterItemType.SwitchSelect]: MSFilterSwitchSelect,
  [ModelScreenerFilterItemType.Range]: MSFilterSlider,
  [ModelScreenerFilterItemType.TrackRecord]: MSFilterTrackRecord,
  [ModelScreenerFilterItemType.PerformancePeriod]: MSFilterCombinedSlider,
  [ModelScreenerFilterItemType.SwitchFavourite]: MSFilterFavourite,
};

export const getMSFilterRenderedItemComponent = (
  itemType: ModelScreenerFilterItemType,
): React.ComponentType<MSFilterRenderedItemProps> | null => {
  const Component = RenderItemComponentMap[itemType];
  if (!Component) return null;
  return Component;
};

const GetRenderedItemPropsMap: Record<
  ModelScreenerFilterItemType | string,
  (
    item: ModelScreenerFilterItemDTO,
  ) =>
    | MSFilterSwitchYesNoInitialProps
    | MSFilterSwitchDropdownInitialProps
    | MSFilterSliderInitialProps
    | MSFilterTrackRecordInitialProps
    | MSFilterCombinedSliderInitialProps
> = {
  [ModelScreenerFilterItemType.SwitchYesNo]:
    getMSFilterSwitchYesNoItemInitialProps,
  [ModelScreenerFilterItemType.SwitchDropdown]:
    getMSFilterSwitchDropdownItemInitialProps,
  [ModelScreenerFilterItemType.SwitchSelect]:
    getMSFilterSwitchSelectItemInitialProps,
  [ModelScreenerFilterItemType.Range]: getMSFilterSliderItemInitialProps,
  [ModelScreenerFilterItemType.TrackRecord]:
    getMSFilterTrackRecordItemInitialProps,
  [ModelScreenerFilterItemType.PerformancePeriod]:
    getMSFilterCombinedSliderInitialProps,
};

export function getMSFilterRenderedItemInitialProps<T>(
  item: ModelScreenerFilterItemDTO,
): T | undefined {
  const getPropsFunction =
    GetRenderedItemPropsMap[item.filterType as ModelScreenerFilterItemType];
  if (!getPropsFunction) return;
  const props = getPropsFunction(item);
  return props as T;
}

// Commons
export const getSingleSelectDropdownDefaultOption = ({
  lov,
  allowNull = false,
}: {
  lov?: ModelScreenerFilterDropdownLov[] | null;
  allowNull?: boolean;
}): ModelScreenerFilterDropdownLov[] | undefined =>
  lov?.map((opt, idx) => ({
    ...opt,
    isSelected: allowNull ? false : idx === 0,
  }));

export const getMSFilterSelectedLovValue = (
  prev: ModelScreenerFilterDropdownLov[],
  onChangeValue: ModelScreenerFilterDropdownLov,
): ModelScreenerFilterDropdownLov[] =>
  prev.map((item) => ({
    ...item,
    isSelected:
      defaultTo(onChangeValue.value, onChangeValue.targetSubHeaderId) ===
      defaultTo(item.value, item.targetSubHeaderId),
  }));

const isEmptySelectedLov = (
  lov?: ModelScreenerFilterDropdownLov[] | null,
): boolean => !lov?.some((item) => !!item.isSelected);

// Process filter functions
type ModelScreenerFilterFunctionParams = {
  rowData: ModelScreenerRow;
  item: ModelScreenerFilterItemDTO;
  subHeaderFlattenedIndexes: Record<string, number>;
  favouritedRowIds?: Record<string, boolean>;
  shouldIncludeNa: boolean;
  shouldIncludeNoData: boolean;
};

type FilterRowParams = {
  rowData: ModelScreenerRow;
  filterItems: FormattedFilterItem[];
  subHeaderFlattenedIndexes: Record<string, number>;
  shouldIncludeNa: boolean;
  shouldIncludeNoData: boolean;
  viaSearchSubHeaderIds?: Record<string, boolean>;
  combinedSearchValues?: Record<string, string[]>;
  basicFilter?: ModelScreenerBasicFilters;
  favouritedRowIds?: Record<string, boolean>;
};

class FormattedFilterItem extends ModelScreenerFilterItemDTO {
  shouldProcess: boolean = false;
}

const isValidSliderFilter = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const { currentMinValue, currentMaxValue, minValue, maxValue } =
    item.data || {};
  const isValid = ![currentMinValue, currentMaxValue, minValue, maxValue].some(
    isNil,
  );

  return {
    isValid: isValid,
    numberOfFiltersApplied: isValid ? 1 : 0,
  };
};

const isValidSwitchYesNoFilter = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const isValid = !isEmptySelectedLov(first(item.data?.options)?.lov);
  return {
    isValid: isValid,
    numberOfFiltersApplied: isValid ? 1 : 0,
  };
};

const isValidSwitchDropdownFilter = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const isValid = !item.data?.options?.some((opt) => {
    return isEmptySelectedLov(opt.lov);
  });

  return {
    isValid: isValid,
    numberOfFiltersApplied: isValid ? 1 : 0,
  };
};

const isValidTrackRecordFilter = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const isValid = !isEmptySelectedLov(item.data?.lov);

  return {
    isValid: isValid,
    numberOfFiltersApplied: isValid ? 1 : 0,
  };
};

const isValidPerformancePeriodFilter = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const defaultResult: ValidateFilterItemResult = {
    isValid: false,
    numberOfFiltersApplied: 0,
  };
  const selectedLov = item.data?.lov?.find((item) => item.isSelected);
  if (!selectedLov || !selectedLov.value) return defaultResult;
  const selectedNestedData: ModelScreenerFilterItemDataDTO[] | undefined =
    item.data?.nestedData?.[selectedLov.value];
  if (!selectedNestedData || isEmpty(selectedNestedData)) return defaultResult;

  let isValid = false;
  let numberOfFiltersApplied = 0;

  selectedNestedData.forEach((itemData) => {
    const pseudoItem: ModelScreenerFilterItemDTO = {
      id: generateUUID(),
      data: itemData,
      filterType: ModelScreenerFilterItemType.Range,
    };

    const isSubItemValidResult = isValidFilterItem(pseudoItem);

    if (!isSubItemValidResult.isValid) return;

    isValid = true;
    numberOfFiltersApplied += isSubItemValidResult.numberOfFiltersApplied;
  });

  return {
    isValid: isValid,
    numberOfFiltersApplied: numberOfFiltersApplied,
  };
};

const isValidFavouriteFilter = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const switchValue = item.data?.switchValue;
  const isValid = Object.values(MSFilterFavouriteSwitchValues).includes(
    switchValue as MSFilterFavouriteSwitchValues,
  );
  return {
    isValid: isValid,
    numberOfFiltersApplied: isValid ? 1 : 0,
  };
};

const ValidateFilterItemFunctionMap: Record<
  ModelScreenerFilterItemType,
  null | ((item: ModelScreenerFilterItemDTO) => ValidateFilterItemResult)
> = {
  [ModelScreenerFilterItemType.SwitchYesNo]: isValidSwitchYesNoFilter,
  [ModelScreenerFilterItemType.SwitchDropdown]: isValidSwitchDropdownFilter,
  [ModelScreenerFilterItemType.SwitchSelect]: isValidSwitchYesNoFilter,
  [ModelScreenerFilterItemType.Range]: isValidSliderFilter,
  [ModelScreenerFilterItemType.TrackRecord]: isValidTrackRecordFilter,
  [ModelScreenerFilterItemType.PerformancePeriod]:
    isValidPerformancePeriodFilter,
  [ModelScreenerFilterItemType.SwitchFavourite]: isValidFavouriteFilter,
};

type ValidateFilterItemResult = {
  isValid: boolean;
  numberOfFiltersApplied: number;
};

const isValidFilterItem = (
  item: ModelScreenerFilterItemDTO,
): ValidateFilterItemResult => {
  const validateFunction =
    ValidateFilterItemFunctionMap[
      item.filterType as ModelScreenerFilterItemType
    ];
  const defaultResult: ValidateFilterItemResult = {
    isValid: false,
    numberOfFiltersApplied: 0,
  };

  if (!validateFunction) return defaultResult;
  const defaultItemValue = getFilterItemDefaultValue(item);
  if (!defaultItemValue || isEqual(item, defaultItemValue))
    return defaultResult;
  return validateFunction(item);
};

export const handleApplyFilter = ({
  formData,
  originalData,
  basicFilter,
  internalData,
}: {
  formData: ModelScreenerFilterDTO;
  originalData: ModelScreenerData;
  basicFilter: ModelScreenerBasicFilters;
  internalData: {
    viaSearchSubHeaderIds: Record<string, boolean>;
    subHeaderFlattenedIndexes: Record<string, number>;
    combinedSearchValues: Record<string, string[]>;
    favouritedRowIds?: Record<string, boolean>;
  };
}): { data: ModelScreenerData; numOfFiltersApplied: number } => {
  const { data: rows, headers } = originalData || {};
  const {
    subHeaderFlattenedIndexes,
    viaSearchSubHeaderIds,
    combinedSearchValues,
    favouritedRowIds,
  } = internalData || {};
  if (
    !formData ||
    isEmpty(subHeaderFlattenedIndexes) ||
    !rows ||
    !basicFilter
  ) {
    return {
      data: originalData,
      numOfFiltersApplied: 0,
    };
  }

  const { shouldIncludeNa, shouldIncludeNoData, columns } = formData;
  let numOfFiltersApplied = 0;

  // Get filter item flat map
  const filterItemFlatMap: FormattedFilterItem[] = columns.flatMap((col) =>
    col.sections?.flatMap((sec) =>
      sec.items?.map((item) => {
        const { isValid, numberOfFiltersApplied } = isValidFilterItem(item);
        numOfFiltersApplied += numberOfFiltersApplied;
        return {
          ...item,
          shouldProcess: isValid,
        };
      }),
    ),
  );

  // Filter rows based on filter items and search value
  const filteredRows = rows.filter((rowData) => {
    return processFilterRowData({
      rowData,
      basicFilter,
      shouldIncludeNoData,
      shouldIncludeNa,
      filterItems: filterItemFlatMap,
      subHeaderFlattenedIndexes,
      viaSearchSubHeaderIds,
      combinedSearchValues,
      favouritedRowIds,
    });
  });

  return {
    data: {
      headers,
      data: filteredRows,
    },
    numOfFiltersApplied: numOfFiltersApplied,
  };
};

const processFilterRowData = ({
  rowData,
  filterItems,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
  viaSearchSubHeaderIds = {},
  combinedSearchValues = {},
  basicFilter,
  favouritedRowIds,
}: FilterRowParams): boolean => {
  let invalidData = false;

  const processSearchValueResult = processSearchValue({
    basicFilter: basicFilter,
    rowData,
    internalValues: {
      viaSearchSubHeaderIds: viaSearchSubHeaderIds || {},
      combinedSearchValue: combinedSearchValues || {},
    },
  });

  if (!processSearchValueResult) return false;

  for (const item of filterItems) {
    if (!item.shouldProcess) continue;

    const isValid = processFilterItem({
      rowData,
      item,
      subHeaderFlattenedIndexes,
      favouritedRowIds,
      shouldIncludeNa,
      shouldIncludeNoData,
    });

    if (!isValid) {
      invalidData = true;
      break;
    }
  }

  return !invalidData;
};

const processSearchValue = ({
  basicFilter,
  rowData,
  internalValues,
}: {
  basicFilter?: ModelScreenerBasicFilters;
  rowData: ModelScreenerRow;
  internalValues: {
    viaSearchSubHeaderIds: Record<string, boolean>;
    combinedSearchValue: Record<string, string[]>;
  };
}): boolean => {
  const { searchValue } = basicFilter || {};
  const hasActiveSearch = !!searchValue?.trim();

  if (!hasActiveSearch) return true;
  const { data, id } = rowData || {};
  const { viaSearchSubHeaderIds, combinedSearchValue } = internalValues || {};
  const lowercaseSearchValue = searchValue?.toLowerCase() ?? "";
  const searchValues = lowercaseSearchValue.split(" ");

  for (let i = 0; i < (data?.length || 0); i++) {
    const cell = data[i];
    const isViaSearchSubHeader = !!viaSearchSubHeaderIds[cell?.subHeaderId];

    if (!isViaSearchSubHeader) continue;

    const viaSearchValueMatched = cell?.value
      ?.toLowerCase()
      .includes(lowercaseSearchValue);
    const viaSearchCombinedValueMatched = compareSearchValues(
      searchValues,
      combinedSearchValue[id],
    );
    const searchValueMatched =
      viaSearchValueMatched || viaSearchCombinedValueMatched;

    if (searchValueMatched) return true;
  }

  return false;
};

const shouldIncludeNAOrNoDataCellValue = (
  shouldIncludeNa: boolean,
  shouldIncludeNoData: boolean,
  cellValue: string,
) =>
  (shouldIncludeNa && cellValue === MSCellDataConstants.NAData) ||
  (shouldIncludeNoData && cellValue === MSCellDataConstants.NoData);

const processSliderItem = ({
  rowData,
  item,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
}: ModelScreenerFilterFunctionParams): boolean => {
  const targetSubHeaderId = item.data.targetSubHeaderId;

  const cellValue =
    targetSubHeaderId &&
    rowData.data[subHeaderFlattenedIndexes[targetSubHeaderId]]?.value;

  if (!cellValue) return false;

  if (
    shouldIncludeNAOrNoDataCellValue(
      shouldIncludeNa,
      shouldIncludeNoData,
      cellValue,
    )
  ) {
    return true;
  }

  const { currentMinValue, currentMaxValue, minValue, maxValue } = item.data;

  const currentMinNum = Number(currentMinValue);
  const currentMaxNum = Number(currentMaxValue);
  const minNum = Number(minValue);
  const maxNum = Number(maxValue);
  const cellValueNum = Number(cellValue);

  const skipFiltering =
    isEqual(currentMinNum, minNum) && isEqual(currentMaxNum, maxNum);
  if (skipFiltering) return true;

  if ([currentMinNum, currentMaxNum, cellValueNum].some(isNaN)) {
    return false;
  }

  return currentMinNum <= cellValueNum && cellValueNum <= currentMaxNum;
};

export const processSwitchYesNoItem = ({
  rowData,
  item,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
}: ModelScreenerFilterFunctionParams) => {
  const switchValue = item.data?.switchValue;
  const selectedOption = item.data?.options?.flatMap((option) =>
    option.lov?.filter((item) => item.isSelected),
  );

  const filterResult: Record<
    string,
    {
      shouldIncludeNaOrNoData: boolean;
      isYes: boolean;
      isNaOrNoDataValue: boolean;
    }
  > = {};

  selectedOption?.forEach((item) => {
    const targetSubHeaderId = item.targetSubHeaderId;
    if (isNil(targetSubHeaderId)) return;

    const cellValue =
      rowData.data[subHeaderFlattenedIndexes[targetSubHeaderId]]?.value;

    const isNaOrNoDataValue =
      isEqual(cellValue, MSCellDataConstants.NAData) ||
      isEqual(cellValue, MSCellDataConstants.NoData);
    const isYes = isEqual(cellValue, MSCellDataConstants.Yes);
    const isValidTextLong =
      targetSubHeaderId === MSSubHeaderIdConstants.InvestmentUniverseOther &&
      !isNil(cellValue) &&
      !isEmpty(cellValue) &&
      cellValue !== MSCellDataConstants.NAData &&
      cellValue !== MSCellDataConstants.NoData;

    const shouldIncludeNaOrNoData = shouldIncludeNAOrNoDataCellValue(
      shouldIncludeNa,
      shouldIncludeNoData,
      cellValue,
    );

    filterResult[targetSubHeaderId] = {
      shouldIncludeNaOrNoData,
      isYes: isYes || isValidTextLong,
      isNaOrNoDataValue,
    };
  });

  const results = Object.values(filterResult);

  switch (switchValue) {
    case MSFilterSwitchValues.Each:
      return results.every(
        (result) => result.shouldIncludeNaOrNoData || result.isYes,
      );
    case MSFilterSwitchValues.Any:
      return results.some(
        (result) => result.shouldIncludeNaOrNoData || result.isYes,
      );
    case MSFilterSwitchValues.Not:
      return results.every(
        (result) =>
          result.shouldIncludeNaOrNoData ||
          (!result.isYes && !result.isNaOrNoDataValue),
      );
    default:
      return true;
  }
};

export const processSwitchDropdownItem = ({
  rowData,
  item,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
}: ModelScreenerFilterFunctionParams) => {
  const filterResult: Record<string, boolean> = {};

  item.data?.options?.forEach((option) => {
    const targetSubHeaderId = option?.targetSubHeaderId;
    if (isNil(targetSubHeaderId)) return;

    const cellValue =
      rowData.data[subHeaderFlattenedIndexes[targetSubHeaderId]]?.value;

    if (
      shouldIncludeNAOrNoDataCellValue(
        shouldIncludeNa,
        shouldIncludeNoData,
        cellValue,
      )
    ) {
      filterResult[targetSubHeaderId] = true;
      return;
    }

    const selectedFilterValue = option.lov.find((lov) => lov.isSelected)?.value;

    if (selectedFilterValue === MSSelectedFilterValues.All) {
      const otherLovValues = option.lov
        .filter((lov) => !lov.isSelected)
        .map((lov) => lov.value);

      filterResult[targetSubHeaderId] = otherLovValues.includes(cellValue);
    } else {
      filterResult[targetSubHeaderId] = isEqual(selectedFilterValue, cellValue);
    }
  });

  return Object.values(filterResult).every(Boolean);
};

export const processTrackRecordItem = ({
  rowData,
  item,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
}: ModelScreenerFilterFunctionParams): boolean => {
  const targetSubHeaderId = item?.data.targetSubHeaderId;
  const cellValue =
    targetSubHeaderId &&
    rowData.data[subHeaderFlattenedIndexes[targetSubHeaderId]].value;

  if (!cellValue) {
    return false;
  }

  const selectedItem = item.data.lov?.find((value) => value.isSelected);

  if (selectedItem?.value) {
    const period = parseInt(selectedItem?.value);
    const currentDate = parse(cellValue, MSDefaultDateFormat, new Date());

    return (
      differenceInYears(new Date(), currentDate) >= period ||
      shouldIncludeNAOrNoDataCellValue(
        shouldIncludeNa,
        shouldIncludeNoData,
        cellValue,
      )
    );
  }

  return true;
};

export const processSwitchSelectItem = ({
  rowData,
  item,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
}: ModelScreenerFilterFunctionParams): boolean => {
  const switchValue = item.data?.switchValue;
  const selectedOption = item.data?.options?.flatMap((option) =>
    option.lov?.filter((item) => item.isSelected),
  );

  const filterResult: Record<string, boolean> = {};

  selectedOption?.forEach((item) => {
    const targetSubHeaderId = item.targetSubHeaderId;
    if (isNil(targetSubHeaderId)) return;

    const cellValue =
      rowData.data[subHeaderFlattenedIndexes[targetSubHeaderId]]?.value;

    filterResult[targetSubHeaderId] =
      shouldIncludeNAOrNoDataCellValue(
        shouldIncludeNa,
        shouldIncludeNoData,
        cellValue,
      ) || isEqual(cellValue, MSCellDataConstants.Yes);
  });

  const values = Object.values(filterResult);

  switch (switchValue) {
    case MSFilterSwitchValues.Each:
      return values.every(Boolean);
    case MSFilterSwitchValues.Any:
      return values.some(Boolean);
    case MSFilterSwitchValues.Not:
      return values.every((value) => !value);
    default:
      return true;
  }
};

export const processPerformanceItem = ({
  rowData,
  item,
  subHeaderFlattenedIndexes,
  shouldIncludeNa,
  shouldIncludeNoData,
}: ModelScreenerFilterFunctionParams): boolean => {
  const selectedPeriod = item.data.lov?.find((period) => period.isSelected);

  if (isNil(selectedPeriod)) return true;

  if (isNil(selectedPeriod.value)) return true;

  const filterCondition = item.data.nestedData?.[selectedPeriod.value];
  if (!filterCondition) return true;

  for (const filterItem of filterCondition) {
    const targetSubHeaderId = filterItem.targetSubHeaderId;
    if (isNil(targetSubHeaderId)) continue;

    const result = processSliderItem({
      rowData,
      item: {
        id: "",
        filterType: "",
        data: filterItem,
      },
      subHeaderFlattenedIndexes,
      shouldIncludeNa,
      shouldIncludeNoData,
    });

    if (!result) return false;
  }

  return true;
};

export const processFavouriteItem = ({
  rowData,
  item,
  favouritedRowIds,
}: ModelScreenerFilterFunctionParams): boolean => {
  const selectedValue = item.data?.switchValue as MSFilterFavouriteSwitchValues;
  if (selectedValue === MSFilterFavouriteSwitchValues.All) {
    return true;
  }
  return !!favouritedRowIds?.[rowData.id];
};

const ProcessFilterItemFunctionMap: Record<
  ModelScreenerFilterItemType,
  null | ((params: ModelScreenerFilterFunctionParams) => boolean)
> = {
  [ModelScreenerFilterItemType.SwitchYesNo]: processSwitchYesNoItem,
  [ModelScreenerFilterItemType.SwitchDropdown]: processSwitchDropdownItem,
  [ModelScreenerFilterItemType.SwitchSelect]: processSwitchSelectItem,
  [ModelScreenerFilterItemType.Range]: processSliderItem,
  [ModelScreenerFilterItemType.TrackRecord]: processTrackRecordItem,
  [ModelScreenerFilterItemType.PerformancePeriod]: processPerformanceItem,
  [ModelScreenerFilterItemType.SwitchFavourite]: processFavouriteItem,
};

const processFilterItem = (
  params: ModelScreenerFilterFunctionParams,
): boolean => {
  const processFunction =
    ProcessFilterItemFunctionMap[
      params.item?.filterType as ModelScreenerFilterItemType
    ];
  if (!processFunction) return true;
  return processFunction(params);
};

const getMSFilterSliderItemDefaultValue = (
  itemData: ModelScreenerFilterItemDataDTO,
): ModelScreenerFilterItemDataDTO => {
  return {
    ...itemData,
    currentMinValue: itemData.minValue,
    currentMaxValue: itemData.maxValue,
  };
};

const getMSFilterTrackRecordItemDefaultValue = (
  itemData: ModelScreenerFilterItemDataDTO,
): ModelScreenerFilterItemDataDTO => {
  return {
    ...itemData,
    lov: getSingleSelectDropdownDefaultOption({ lov: itemData.lov }) ?? null,
  };
};

const getMSFilterSwitchYesNoItemDefaultValue = (
  itemData: ModelScreenerFilterItemDataDTO,
): ModelScreenerFilterItemDataDTO => {
  return {
    ...itemData,
    switchValue: MSFilterDefaultValueConstants.SwitchValue,
    options:
      itemData.options?.map((opt) => ({
        ...opt,
        lov:
          getSingleSelectDropdownDefaultOption({
            lov: opt.lov,
            allowNull: true,
          }) ?? [],
      })) ?? null,
  };
};

const getMSFilterSwitchSelectItemDefaultValue =
  getMSFilterSwitchYesNoItemDefaultValue;

const getMSFilterSwitchDropdownItemDefaultValue = (
  itemData: ModelScreenerFilterItemDataDTO,
): ModelScreenerFilterItemDataDTO => {
  return {
    ...itemData,
    options:
      itemData.options?.map((opt) => ({
        ...opt,
        lov:
          getSingleSelectDropdownDefaultOption({
            lov: opt.lov,
          }) ?? [],
      })) ?? null,
  };
};

const getMSFilterCombinedSliderItemDefaultValue = (
  itemData: ModelScreenerFilterItemDataDTO,
): ModelScreenerFilterItemDataDTO => {
  const clonedItemData = cloneDeep(itemData);

  clonedItemData.lov?.forEach((value) => {
    value.isSelected = false;

    if (clonedItemData.nestedData && value.value) {
      const nestedDataArray = clonedItemData.nestedData[value.value];
      if (nestedDataArray) {
        for (let i = 0; i < nestedDataArray.length; i++) {
          nestedDataArray[i] = getMSFilterSliderItemDefaultValue(
            nestedDataArray[i],
          );
        }
      }
    }
  });

  return clonedItemData;
};

const getMSFilterFavouriteItemDefaultValue = (
  itemData: ModelScreenerFilterItemDataDTO,
): ModelScreenerFilterItemDataDTO => {
  return {
    ...itemData,
    switchValue: MSFilterDefaultValueConstants.FavouriteSwitchValue,
  };
};

const GetFilterItemDefaultValueMap: Record<
  ModelScreenerFilterItemType,
  (item: ModelScreenerFilterItemDataDTO) => ModelScreenerFilterItemDataDTO
> = {
  [ModelScreenerFilterItemType.SwitchYesNo]:
    getMSFilterSwitchYesNoItemDefaultValue,
  [ModelScreenerFilterItemType.SwitchDropdown]:
    getMSFilterSwitchDropdownItemDefaultValue,
  [ModelScreenerFilterItemType.SwitchSelect]:
    getMSFilterSwitchSelectItemDefaultValue,
  [ModelScreenerFilterItemType.Range]: getMSFilterSliderItemDefaultValue,
  [ModelScreenerFilterItemType.TrackRecord]:
    getMSFilterTrackRecordItemDefaultValue,
  [ModelScreenerFilterItemType.PerformancePeriod]:
    getMSFilterCombinedSliderItemDefaultValue,
  [ModelScreenerFilterItemType.SwitchFavourite]: getMSFilterFavouriteItemDefaultValue,
};

export const getFilterItemDefaultValue = (
  item: ModelScreenerFilterItemDTO,
): ModelScreenerFilterItemDTO | undefined => {
  const filterType = item.filterType as ModelScreenerFilterItemType;
  const getFilterItemDefaultValueFunction = filterType
    ? GetFilterItemDefaultValueMap[filterType]
    : null;
  if (!getFilterItemDefaultValueFunction) return;

  const itemData = getFilterItemDefaultValueFunction(item.data);
  return {
    ...item,
    data: itemData,
  };
};

export const getModelScreenerDefaultMainFilter = (
  formData: ModelScreenerFilterDTO | null,
): ModelScreenerFilterDTO | null => {
  if (!formData) return null;
  const clonedFilterDTO = cloneDeep(formData);

  clonedFilterDTO.id = MSFilterDefaultValueConstants.MSDefaultFilterId;
  clonedFilterDTO.shouldIncludeNoData = false;
  clonedFilterDTO.shouldIncludeNa = false;
  clonedFilterDTO.name = MSFilterDefaultValueConstants.MSDefaultFilterName;
  clonedFilterDTO.isSystemDefault = true;
  clonedFilterDTO.columns.forEach((col) => {
    const sections = col.sections || [];
    sections.forEach((sec) => {
      sec.items = sec.items?.map((item) => {
        const itemDefaultValue = getFilterItemDefaultValue(item);
        return itemDefaultValue || item;
      });
    });
  });

  return clonedFilterDTO;
};
